import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import format from 'date-fns/format';
// eslint-disable-next-line no-restricted-imports
import { tv } from 'tailwind-variants';

import { PdfTable } from './Table';
import { DocumentTitle } from './components/DocumentTitle';
import { LocationHeader } from './components/LocationHeader';
import { PageFooter } from './components/PageFooter';
import { tw } from './pdf-tw';
import { commonStyles } from './styles';
import { PdfLocation, PdfPatient, PdfTenant } from './types';

export type SuperBillLineItem = {
  code: string;
  description: string;
  modifier: string;
  diagnosisPointer: string;
  fee: string;
  quantity: string;
  total: string;
};

export type SuperbillProps = {
  tenant: PdfTenant;
  patientInfo: PdfPatient;
  location?: PdfLocation;
  date: string;
  providerName: string;
  lineItems: SuperBillLineItem[];
  diagnosisCodes?: {
    id: string;
    number: string;
    code: string;
    description: string;
  }[];
  totalCharges: string;
  signatureUrl?: string;
  signatureData?: Buffer;
};

const styles = tv({
  slots: {
    section: 'flex w-full flex-col gap-2',
    sectionHeader: 'w-full bg-bgStrong px-2 pb-1 pt-2 text-body-md',
    summaryRow: commonStyles.body({
      className: 'flex w-full flex-row justify-end gap-6 px-2',
    }),
    signatureContainer: commonStyles.body({
      className:
        'mt-12 flex w-full flex-1 flex-row justify-between font-semibold',
    }),
    signatureColumn: 'flex flex-col justify-end gap-2',
  },
})();

const SectionHeader = ({ text }: { text: string }) => {
  return (
    <View style={tw(styles.sectionHeader())}>
      <Text style={tw('text-textStrong')}>{text}</Text>
    </View>
  );
};

export const SuperbillPdf = (props: SuperbillProps) => {
  const {
    tenant,
    patientInfo,
    lineItems,
    totalCharges,
    providerName,
    diagnosisCodes = [],
    location = {},
    date,
    signatureUrl,
    signatureData,
  } = props;

  let placeOfService = '';
  if (location.businessName) {
    placeOfService += location.businessName;
  }
  if (location.placeOfServiceCode) {
    placeOfService += `\nPlace of Service Code: ${location.placeOfServiceCode}`;
  }
  if (location.address) {
    placeOfService += `\n${location.address}`;
  }

  const signatureSource = signatureUrl ?? signatureData;

  return (
    <Document>
      <Page
        size="LETTER"
        style={tw(
          'py-[0.5in] px-[0.6in] text-black font-[Inter] font-medium gap-2',
        )}
      >
        <LocationHeader tenant={tenant} location={location} showTaxId />

        <DocumentTitle title="Superbill" />

        <View style={tw(styles.section())}>
          <View wrap={false} style={tw(styles.section())}>
            <SectionHeader text="Session Information" />
            <PdfTable<'date' | 'provider' | 'placeOfService'>
              columns={{
                date: {
                  label: 'Date of Visit',
                  width: 20,
                },
                provider: {
                  width: 30,
                },
                placeOfService: {
                  label: 'Place of Service',
                  width: 50,
                },
              }}
              rows={[
                {
                  date: date,
                  provider: providerName,
                  placeOfService: placeOfService || '--',
                },
              ]}
            />
          </View>

          <View wrap={false} style={tw(styles.section())}>
            <SectionHeader text="Patient Information" />
            <PdfTable<'name' | 'dateOfBirth' | 'address'>
              columns={{
                name: {
                  width: 20,
                },
                dateOfBirth: {
                  label: 'Date of Birth',
                  width: 30,
                },
                address: {
                  width: 50,
                },
              }}
              rows={[
                {
                  name: patientInfo.name,
                  dateOfBirth: patientInfo.dateOfBirth,
                  address: patientInfo.address ?? '--',
                },
              ]}
            />
          </View>

          <View style={tw(styles.section())}>
            <SectionHeader text="Diagnosis" />
            <PdfTable<'number' | 'code' | 'description'>
              columns={{
                number: {
                  label: '#',
                  width: 20,
                },
                code: {
                  width: 30,
                },
                description: {
                  width: 50,
                },
              }}
              rows={diagnosisCodes.map((dc, i) => ({
                number: `${i + 1}`,
                code: dc.code,
                description: dc.description,
              }))}
            />
          </View>

          <View style={tw(styles.section())}>
            <SectionHeader text="Session" />
            <PdfTable<
              | 'code'
              | 'description'
              | 'modifier'
              | 'diagnosisPointer'
              | 'fee'
              | 'qty'
              | 'total'
            >
              columns={{
                code: {
                  label: 'Billing Code',
                  width: 15,
                },
                description: {
                  width: 30,
                },
                modifier: {
                  alignText: 'center',
                  width: 14,
                },
                diagnosisPointer: {
                  alignText: 'center',
                  width: 17,
                },
                fee: {
                  alignText: 'right',
                  width: 8,
                },
                qty: {
                  alignText: 'right',
                  width: 8,
                },
                total: {
                  alignText: 'right',
                  width: 8,
                },
              }}
              rows={lineItems.map((item) => ({
                ...item,
                qty: item.quantity,
              }))}
            />
          </View>

          <View wrap={false} style={tw(styles.section())}>
            <SectionHeader text="Summary" />
            <View style={tw(styles.summaryRow())}>
              <Text style={tw('font-semibold')}>Total Charges</Text>
              <Text>{totalCharges}</Text>
            </View>
          </View>
        </View>

        <View wrap={false} style={tw(styles.signatureContainer())}>
          <View style={tw(styles.signatureColumn())}>
            <Text>Patient Signature</Text>
            {signatureSource ? (
              <Image
                style={tw('max-w-32 max-h-8 mt-2')}
                src={signatureSource}
              ></Image>
            ) : (
              <View style={tw('h-8 w-8 mt-2')} />
            )}
            <Text>Provider Signature</Text>
          </View>
          <View style={tw(styles.signatureColumn({ className: 'mr-12' }))}>
            <Text>Date</Text>
            <Text style={tw('font-normal mb-2 mt-4')}>
              {format(Date.now(), 'MMMM dd, yyyy')}
            </Text>
            <Text>Date</Text>
          </View>
        </View>

        <PageFooter />
      </Page>
    </Document>
  );
};
